import type { PaletteMode } from "@mui/material"
import React, { createContext, useCallback, useContext, useMemo } from "react"

import useLocalStorage from "@sap/hooks/useLocalStorage"

import { defaultSettings } from "./config"
import { defaultPreset, getPresets, presetsOption } from "./presets"

const initialState = {
  ...defaultSettings,
  // Mode
  onToggleMode: () => {},
  onChangeMode: () => {},
  // Contrast
  onToggleContrast: () => {},
  onChangeContrast: () => {},
  // Layout
  onToggleLayout: () => {},
  // Color
  onChangeColorPresets: () => {},
  presetsColor: defaultPreset,
  presetsOption: [],
  // Stretch
  onToggleStretch: () => {},
  // Reset
  onResetSetting: () => {},
}

// ----------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components
export const SettingsContext = createContext(initialState)

// eslint-disable-next-line react-refresh/only-export-components
export const useSettingsContext = () => {
  const context = useContext(SettingsContext)

  if (!context) throw new Error("useSettingsContext must be use inside SettingsProvider")

  return context
}

// ----------------------------------------------------------------------

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useLocalStorage("settings", defaultSettings)

  // Mode
  const onToggleMode = useCallback(() => {
    const themeMode = settings.themeMode === "light" ? "dark" : "light"
    setSettings({ ...settings, themeMode })
  }, [setSettings, settings])

  const onChangeMode = useCallback(
    (event: { target: { value: PaletteMode } }) => {
      const themeMode = event.target.value
      setSettings({ ...settings, themeMode })
    },
    [setSettings, settings]
  )

  // Layout
  const onToggleLayout = useCallback(() => {
    const themeLayout = settings.themeLayout === "vertical" ? "mini" : "vertical"
    setSettings({ ...settings, themeLayout })
  }, [setSettings, settings])

  // Contrast
  const onToggleContrast = useCallback(() => {
    const themeContrast = settings.themeContrast === "default" ? "bold" : "default"
    setSettings({ ...settings, themeContrast })
  }, [setSettings, settings])

  const onChangeContrast = useCallback(
    (event: { target: { value: string } }) => {
      const themeContrast = event.target.value
      setSettings({ ...settings, themeContrast })
    },
    [setSettings, settings]
  )

  // Color
  const onChangeColorPresets = useCallback(
    (event: { target: { value: string } }) => {
      const themeColorPresets = event.target.value
      setSettings({ ...settings, themeColorPresets })
    },
    [setSettings, settings]
  )

  // Stretch
  const onToggleStretch = useCallback(() => {
    const themeStretch = !settings.themeStretch
    setSettings({ ...settings, themeStretch })
  }, [setSettings, settings])

  // Reset
  const onResetSetting = useCallback(() => {
    setSettings(defaultSettings)
  }, [setSettings])

  const value = useMemo(
    () => ({
      ...settings,
      // Mode
      onToggleMode,
      onChangeMode,
      // Layout
      onToggleLayout,
      // Contrast
      onChangeContrast,
      onToggleContrast,
      // Stretch
      onToggleStretch,
      // Color
      onChangeColorPresets,
      presetsOption,
      presetsColor: getPresets(settings.themeColorPresets),
      // Reset
      onResetSetting,
    }),
    [
      settings,
      onToggleMode,
      onChangeMode,
      onToggleLayout,
      onChangeContrast,
      onToggleContrast,
      onToggleStretch,
      onChangeColorPresets,
      onResetSetting,
    ]
  )

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}
